// frontend/src/pages/RaioXAssinaturasPage/RaioXAssinaturasPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography, Breadcrumb, Card, Row, Col, List, Button, Space, Empty, Spin, message,
} from 'antd';
import {
    HomeOutlined, WalletOutlined, QuestionCircleOutlined, CheckOutlined, CloseOutlined,
} from '@ant-design/icons';
import { getRaioXAssinaturas, definirNaturezaRecorrencia } from '../../services/recurrenceService';
import { getErrorMessage } from '../../utils/errorHandler';
import { usePrivacy } from '../../contexts/PrivacyContext';

const { Title, Text, Paragraph } = Typography;

const FREQ_LABEL = { SEMANAL: 'Semanal', QUINZENAL: 'Quinzenal', MENSAL: 'Mensal', ANUAL: 'Anual' };

const formatBRL = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const RaioXAssinaturasPage = () => {
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [classificandoId, setClassificandoId] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const { isPrivate } = usePrivacy();
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const resp = await getRaioXAssinaturas();
            setDados(resp);
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Falha ao buscar o raio-X de assinaturas.'));
        } finally {
            setLoading(false);
        }
    }, [messageApi]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const classificar = async (id, naturezaGasto) => {
        setClassificandoId(id);
        try {
            await definirNaturezaRecorrencia(id, naturezaGasto);
            await fetchData();
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Não foi possível salvar a classificação.'));
        } finally {
            setClassificandoId(null);
        }
    };

    const itemMeta = (item) => (
        <Row justify="space-between" align="middle" wrap style={{ width: '100%', gap: 8 }}>
            <Col style={{ minWidth: 0, flex: '1 1 200px' }}>
                <Text strong ellipsis>{item.descricao}</Text>
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {FREQ_LABEL[item.frequencia] || item.frequencia}
                        {item.frequencia !== 'MENSAL' && ' · equivalente/mês'}
                    </Text>
                </div>
            </Col>
            <Col style={{ flexShrink: 0, fontWeight: 600 }}>
                {isPrivate ? '****' : formatBRL(item.valor_mensal_equivalente)}
            </Col>
        </Row>
    );

    if (loading && !dados) {
        return (
            <div style={{ padding: 48, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    const assinaturas = dados?.assinaturas || [];
    const pendentes = dados?.pendentes_revisao || [];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: '100%', overflowX: 'hidden' }}>
            {contextHolder}
            <div style={{ marginBottom: 24 }}>
                <Breadcrumb
                    items={[
                        { href: '/meu-desempenho', title: <HomeOutlined /> },
                        { href: '/recorrencias', title: 'Recorrências' },
                        { title: 'Raio-X de Assinaturas' },
                    ]}
                    style={{ marginBottom: 8 }}
                />
                <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Raio-X de Assinaturas</Title>
                <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
                    Só entram aqui recorrências que você (ou a gente) já identificou como assinatura
                    de serviço digital — contas fixas como saúde, água, luz ou condomínio ficam de fora.
                </Paragraph>
            </div>

            <Card style={{ marginBottom: 24 }}>
                <Space direction="vertical" size={0}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        <WalletOutlined /> Total em assinaturas / mês
                    </Text>
                    <Text style={{ fontSize: 28, fontWeight: 700, color: '#dc3545' }}>
                        {isPrivate ? '****' : formatBRL(dados?.total_mensal)}
                    </Text>
                </Space>
            </Card>

            <Card
                title={`Assinaturas identificadas (${assinaturas.length})`}
                style={{ marginBottom: 24 }}
            >
                {assinaturas.length === 0 ? (
                    <Empty description="Nenhuma assinatura identificada ainda." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <List
                        dataSource={assinaturas}
                        renderItem={(item) => (
                            <List.Item
                                actions={[
                                    <Button
                                        key="nao-e"
                                        size="small"
                                        type="text"
                                        loading={classificandoId === item.id}
                                        onClick={() => classificar(item.id, 'CONTA_FIXA')}
                                    >
                                        Não é assinatura
                                    </Button>,
                                ]}
                            >
                                {itemMeta(item)}
                            </List.Item>
                        )}
                    />
                )}
            </Card>

            {pendentes.length > 0 && (
                <Card
                    title={(
                        <Space>
                            <QuestionCircleOutlined style={{ color: '#faad14' }} />
                            <span>Ainda não classificadas ({pendentes.length})</span>
                        </Space>
                    )}
                >
                    <Paragraph type="secondary" style={{ marginTop: -8 }}>
                        Não conseguimos identificar sozinhos — diga se é uma assinatura (cancelável
                        quando quiser) ou uma conta fixa do dia a dia.
                    </Paragraph>
                    <List
                        dataSource={pendentes}
                        renderItem={(item) => (
                            <List.Item
                                actions={[
                                    <Button
                                        key="assinatura"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        loading={classificandoId === item.id}
                                        onClick={() => classificar(item.id, 'ASSINATURA')}
                                    >
                                        É assinatura
                                    </Button>,
                                    <Button
                                        key="conta-fixa"
                                        size="small"
                                        icon={<CloseOutlined />}
                                        loading={classificandoId === item.id}
                                        onClick={() => classificar(item.id, 'CONTA_FIXA')}
                                    >
                                        Conta fixa
                                    </Button>,
                                ]}
                            >
                                {itemMeta(item)}
                            </List.Item>
                        )}
                    />
                </Card>
            )}
        </div>
    );
};

export default RaioXAssinaturasPage;
